import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Input,
  Select,
  SimpleGrid,
  Text,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  VStack,
  Checkbox,
} from '@chakra-ui/react';
import InputField from 'components/fields/InputField';
import { vendorRepository, VendorName } from 'api/vendorRepository';
import { editProductSchema, flattenZodErrors } from 'validations/Validations';

interface UpdateProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId: string | null;
  vendorProductId: string | null;
  onProductUpdated: () => void;
  showCenteredAlert: (
    message: string,
    status: 'success' | 'error' | 'warning'
  ) => void;
}

export default function UpdateProductModal({
  isOpen,
  onClose,
  productId,
  vendorProductId,
  onProductUpdated,
  showCenteredAlert,
}: UpdateProductModalProps) {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const bgColor = useColorModeValue('white', 'navy.700');

  const [hasAMC, setHasAMC] = useState(false);
  const [vendorNames, setVendorNames] = useState<VendorName[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [name, setName] = useState('');
  const [productType, setProductType] = useState('');
  // const [productTypes, setProductTypes] = useState<any[]>([]);
  const [description, setDescription] = useState('');
  const [maintenanceSchedule, setMaintenanceSchedule] = useState('');
  const [productStatus, setProductStatus] = useState('Active');
  const [location, setLocation] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const today = new Date().toISOString().split('T')[0];
  const inputBg = useColorModeValue('white', 'navy.700');
  const menuListBg = useColorModeValue('white', 'navy.800');
  const placeholderColor = useColorModeValue('gray.500', 'gray.400');

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setter(e.target.value);
      };


  const handleUpdate = async () => {
    const formData = {
      selectedVendor: selectedVendorId
        ? { vendorId: selectedVendorId, name: '' }
        : null,
      name,
      productType,
      description,
      productStatus,
      maintenanceSchedule,
      location,
      hasAMC,
    };
    try {
      editProductSchema.parse(formData);
      setErrors({});
    } catch (err: any) {
      if (err.issues) {
        setErrors(flattenZodErrors(err));
        showCenteredAlert('Please fix the highlighted errors', 'error');
        return;
      }
    }
    const productUpdate: any = {};

    if (name) productUpdate.productName = name;
    if (productType) productUpdate.productType = productType;
    if (description) productUpdate.productDescription = description;
    if (productStatus) productUpdate.productStatus = productStatus;
    if (location) productUpdate.location = location;
    productUpdate.hasAMC = hasAMC;

    if (maintenanceSchedule) {
      productUpdate.maintenanceSchedule = {
        maintenanceDetails: maintenanceSchedule,
        status: 'Scheduled',
      };
    }

    if (!vendorProductId || !productId) {
      showCenteredAlert('Invalid product reference', 'error');
      return;
    }

    if (Object.keys(productUpdate).length === 0) {
      showCenteredAlert('No changes detected to update', 'warning');
      return;
    }
    try {
      setIsUpdating(true);

      const response = await vendorRepository.updateVendorProduct(
        vendorProductId,
        productId,
        productUpdate,
        imageFile
      );

      if (response?.fileUrl) {
        setImagePreview(response.fileUrl);
      }

      await vendorRepository.getVendorProductById(
        vendorProductId,
        productId
      );
      showCenteredAlert('Product updated successfully', 'success');
      onProductUpdated?.();
      onClose();
    } catch (error) {
      showCenteredAlert('Failed to update product', 'error');
    } finally {
      setIsUpdating(false);
    }
  };
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setImageFile(file);

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!productId || !vendorProductId) return;

    const fetchData = async () => {
      try {
        const vendors = await vendorRepository.getVendorNames();
        setVendorNames(vendors);

        const product = await vendorRepository.getVendorProductById(
          vendorProductId,
          productId
        );

        if (!product) return;

        setProductType(product.productType || '');
        setName(product.productName || '');
        setDescription(product.productDescription || '');
        setProductStatus(product.productStatus || 'Active');
        setLocation(product.location || '');
        setHasAMC(product.hasAMC ?? false);

        setMaintenanceSchedule(
          product.maintenanceSchedule?.maintenanceDetails || ''
        );
        if (product.productImage) {
          setImagePreview(product.productImage);
        };
      } catch (error) {
        showCenteredAlert('Failed to load product details', 'error');
      }
    };

    fetchData();
  }, [isOpen, productId, vendorProductId]);


  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg={menuListBg}>Edit Vendor Product</ModalHeader>
        <ModalCloseButton />
        <ModalBody bg={menuListBg}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                  Product Name
                </Text>
                <Input
                  placeholder="e.g. Digital Meter"
                  value={name}
                  onChange={handleInputChange(setName)}
                  h="44px"
                  fontSize="sm"
                  color={textColor}
                  isInvalid={!!errors.name}
                />
                {errors.name && (
                  <Text fontSize="xs" color="red.500">
                    {errors.name}
                  </Text>
                )}
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Product Status
                </Text>
                <Select
                  placeholder="Select status"
                  h="44px"
                  fontSize="sm"
                  value={productStatus}
                  onChange={(e) => setProductStatus(e.target.value)}
                  bg={inputBg}
                  color={productStatus ? textColor : placeholderColor}
                >
                  <option style={{ backgroundColor: menuListBg, color: textColor }} value="Active">Active</option>
                  <option style={{ backgroundColor: menuListBg, color: textColor }} value="Inactive">Inactive</option>
                </Select>
                {errors.productStatus && (
                  <Text fontSize="xs" color="red.500">
                    {errors.productStatus}
                  </Text>
                )}
              </Box>
            </VStack>

            <VStack spacing={6} align="stretch">
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb="8px">
                  Product Type
                </Text>
                <Input
                  placeholder="e.g. Digital Meter"
                  value={productType}
                  onChange={handleInputChange(setProductType)}
                  h="44px"
                  fontSize="sm"
                  color={textColor}
                  isInvalid={!!errors.productType}
                  readOnly
                />
                {errors.productType && (
                  <Text fontSize="xs" color="red.500">
                    {errors.productType}
                  </Text>
                )}
              </Box>
              <Box>
                <InputField
                  id="description"
                  label="Product Description"
                  placeholder="Enter detailed description"
                  value={description}
                  onChange={handleInputChange(setDescription)}
                  isTextarea
                  error={errors.description}
                  bg={useColorModeValue("white", "gray.700")}
                />
              </Box>
            </VStack>
          </SimpleGrid>

          <Box mt={6}>
            <InputField
              id="maintenanceSchedule"
              label="Maintenance Schedule Details"
              placeholder="e.g. Quarterly calibration and cleaning"
              value={maintenanceSchedule}
              onChange={handleInputChange(setMaintenanceSchedule)}
              isTextarea
              error={errors.maintenanceSchedule}
              bg={useColorModeValue("white", "gray.700")}
            />
          </Box>

          <Box mt={6}>
            <Text fontSize="sm" fontWeight="bold" mb="8px">
              Product Image
            </Text>

            <Box mb={3}>
              <Box
                as="img"
                src={imagePreview}
                alt="Preview"
                boxSize="120px"
                objectFit="cover"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
                _hover={{ opacity: 0.8 }}
                transition="0.2s"
              />
            </Box>
            <Input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              p={1}
            />

          </Box>
          <Box>
            <Checkbox
              isChecked={hasAMC}
              onChange={(e) => setHasAMC(e.target.checked)}
              colorScheme="blue"
            >
              Is AMC required for this product?
            </Checkbox>
          </Box>
        </ModalBody>

        <ModalFooter justifyContent="center" bg={menuListBg}>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleUpdate}
            isLoading={isUpdating}
          >
            Update
          </Button>
          <Button variant="outline" borderColor="gray.300" onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
